import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, TextField, MenuItem,
    Button, Divider, CircularProgress, Alert
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArticleIcon from '@mui/icons-material/Article';
import { TaxService } from '../../services/tax.service';
import { ITaxYear, ITaxReportMonthly, IEmployeeTaxSummary } from '../../interfaces/tax.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(0, i).toLocaleString('default', { month: 'long' })
}));

const TaxReports: React.FC = () => {
    const { showError } = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [taxYears, setTaxYears] = useState<ITaxYear[]>([]);
    const [selectedYearId, setSelectedYearId] = useState('');
    const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());
    const [monthlyMonth, setMonthlyMonth] = useState(new Date().getMonth() + 1);
    const [monthlySummary, setMonthlySummary] = useState<ITaxReportMonthly | null>(null);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [employees, setEmployees] = useState<IEmployeeTaxSummary[]>([]);
    const [loadingMonthly, setLoadingMonthly] = useState(false);
    const [yearsLoaded, setYearsLoaded] = useState(false);

    useEffect(() => {
        TaxService.getTaxYears(institutionId)
            .then(years => {
                setTaxYears(years);
                const active = years.find(y => y.isActive) ?? years[0];
                if (active) setSelectedYearId(active.id!);
            })
            .catch(() => showError('Failed to load tax years'))
            .finally(() => setYearsLoaded(true));
    }, [institutionId]);

    useEffect(() => {
        if (!selectedYearId) return;
        TaxService.getEmployeeTaxSummary(selectedYearId, institutionId)
            .then(setEmployees)
            .catch(() => showError('Failed to load employee list'));
    }, [selectedYearId, institutionId]);

    const loadMonthly = async () => {
        if (!selectedYearId) return;
        setLoadingMonthly(true);
        try {
            const s = await TaxService.getMonthlySummary(selectedYearId, monthlyYear, monthlyMonth, institutionId);
            setMonthlySummary(s);
        } catch { showError('Failed to load monthly summary'); }
        finally { setLoadingMonthly(false); }
    };

    const downloadMonthlyPdf = async () => {
        if (!selectedYearId) return;
        try {
            const blob = await TaxService.downloadMonthlySummaryPdf(selectedYearId, monthlyYear, monthlyMonth, institutionId);
            triggerDownload(blob, `tax-summary-${monthlyYear}-${monthlyMonth}.pdf`);
        } catch { showError('Download failed'); }
    };

    const downloadCertificate = async () => {
        if (!selectedYearId || !selectedEmployee) return;
        try {
            const blob = await TaxService.downloadCertificatePdf(selectedYearId, selectedEmployee, institutionId);
            const emp = employees.find(e => e.userId === selectedEmployee);
            const who = (emp?.userName ?? 'employee').replace(/[^A-Za-z0-9._-]+/g, '_');
            triggerDownload(blob, `tax-certificate-${who}.pdf`);
        } catch { showError('Download failed'); }
    };

    const triggerDownload = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Box data-help-page="tax.reports.page" p={3}>
            <Typography variant="h5" mb={3}>Tax Reports</Typography>
            {yearsLoaded && taxYears.length === 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    No tax year has been set up yet, so there is nothing to report. Add a tax year and its
                    slabs under Tax Settings, then come back here.
                </Alert>
            )}
            <Box display="flex" gap={3} flexWrap="wrap">

                {/* Monthly Summary */}
                <Paper data-help-section="tax.reports.section.monthly" sx={{ flex: 1, minWidth: 300, p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <CalendarMonthIcon color="primary" />
                        <Typography variant="h6">Monthly Tax Summary</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Total tax deducted across all employees for a selected month.
                    </Typography>
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                        <TextField data-help="tax.reports.monthly.field.taxYear" select size="small" label="Tax Year" value={selectedYearId}
                            onChange={e => setSelectedYearId(e.target.value)} sx={{ flex: '1 1 130px', maxWidth: 180 }}>
                            {taxYears.map(y => <MenuItem key={y.id} value={y.id}>{y.label}</MenuItem>)}
                        </TextField>
                        <TextField data-help="tax.reports.monthly.field.month" select size="small" label="Month" value={monthlyMonth}
                            onChange={e => setMonthlyMonth(parseInt(e.target.value))} sx={{ flex: '1 1 120px', maxWidth: 170 }}>
                            {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </TextField>
                        <TextField data-help="tax.reports.monthly.field.year" size="small" label="Year" type="number" value={monthlyYear}
                            onChange={e => setMonthlyYear(parseInt(e.target.value))} sx={{ flex: '1 1 90px', maxWidth: 120 }} />
                    </Box>
                    <Button data-help="tax.reports.monthly.load" variant="outlined" size="small" onClick={loadMonthly} disabled={!selectedYearId} sx={{ mb: 2 }}>Load</Button>

                    {loadingMonthly && <CircularProgress size={20} />}
                    {monthlySummary && monthlySummary.totalEmployees === 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            No tax was calculated for this month. Run payroll for the month first, then load again.
                        </Alert>
                    )}
                    {monthlySummary && monthlySummary.totalEmployees > 0 && (
                        <Box sx={{ background: '#f8fafc', borderRadius: 1, p: 2, mb: 2 }}>
                            {[
                                ['Total Employees', monthlySummary.totalEmployees],
                                ['Total Gross Earnings', monthlySummary.totalGrossEarnings?.toLocaleString()],
                                ['Total Exemptions', monthlySummary.totalExemptions?.toLocaleString()],
                            ].map(([k, v]) => (
                                <Box key={k as string} display="flex" justifyContent="space-between" py={0.5}>
                                    <Typography variant="body2" color="text.secondary">{k}</Typography>
                                    <Typography variant="body2" fontWeight={500}>{v}</Typography>
                                </Box>
                            ))}
                            <Divider sx={{ my: 0.5 }} />
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2" color="primary.main" fontWeight={700}>Total Tax Deducted</Typography>
                                <Typography variant="body2" color="primary.main" fontWeight={700}>
                                    {monthlySummary.totalTaxDeducted?.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Button data-help="tax.reports.monthly.pdf" variant="contained" size="small" onClick={downloadMonthlyPdf} disabled={!selectedYearId}>Download PDF</Button>
                </Paper>

                {/* Annual Certificate */}
                <Paper data-help-section="tax.reports.section.certificate" sx={{ flex: 1, minWidth: 300, p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <ArticleIcon color="primary" />
                        <Typography variant="h6">Annual Tax Certificate</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Per-employee certificate. Equivalent to TDS cert (BD), EA Form (MY), P60 (UK), W-2 (US).
                    </Typography>
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                        <TextField data-help="tax.reports.cert.field.taxYear" select size="small" label="Tax Year" value={selectedYearId}
                            onChange={e => setSelectedYearId(e.target.value)} sx={{ flex: '1 1 130px', maxWidth: 180 }}>
                            {taxYears.map(y => <MenuItem key={y.id} value={y.id}>{y.label}</MenuItem>)}
                        </TextField>
                        <TextField data-help="tax.reports.cert.field.employee" select size="small" label="Employee" value={selectedEmployee}
                            onChange={e => setSelectedEmployee(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 260 }}>
                            {employees.map(e => <MenuItem key={e.userId} value={e.userId}>{e.userName}</MenuItem>)}
                        </TextField>
                    </Box>
                    {selectedEmployee && (() => {
                        const emp = employees.find(e => e.userId === selectedEmployee);
                        return emp ? (
                            <Box sx={{ background: '#f8fafc', borderRadius: 1, p: 2, mb: 2 }}>
                                {[
                                    ['Taxable Income YTD', emp.taxableIncomeYtd?.toLocaleString()],
                                    ['Total Tax Deducted', emp.taxDeductedYtd?.toLocaleString()],
                                ].map(([k, v]) => (
                                    <Box key={k as string} display="flex" justifyContent="space-between" py={0.5}>
                                        <Typography variant="body2" color="text.secondary">{k}</Typography>
                                        <Typography variant="body2" fontWeight={500}>{v}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : null;
                    })()}
                    <Button data-help="tax.reports.cert.pdf" variant="contained" size="small" onClick={downloadCertificate} disabled={!selectedYearId || !selectedEmployee}>
                        Download PDF
                    </Button>
                </Paper>
            </Box>
        </Box>
    );
};

export default TaxReports;
